import React, { useState, useEffect } from "react";

/**
 * Universal section jumps navigation: vertical line with dots.
 * sections: Array<{ id, label }>; optional scrollRootRef for modal; optional expanded for parent-controlled hover.
 */
export default function SectionJumpsNav({
  sections = [],
  scrollRootRef = null,
  className = "",
  onExpandChange,
  expanded: controlledExpanded,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const expanded =
    controlledExpanded !== undefined && controlledExpanded !== null
      ? controlledExpanded
      : isExpanded;

  useEffect(() => {
    onExpandChange?.(expanded);
  }, [expanded, onExpandChange]);

  useEffect(() => {
    if (!sections.length) return;
    const container = scrollRootRef?.current ?? null;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        const best = visible.reduce((a, b) =>
          (b.intersectionRatio ?? 0) > (a.intersectionRatio ?? 0) ? b : a
        );
        setActiveId(best.target.id);
      },
      {
        root: container || null,
        rootMargin: "-15% 0px -50% 0px",
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
      }
    );
    const observe = (cont) => {
      sections.forEach(({ id }) => {
        const el = cont
          ? cont.querySelector(`#${CSS.escape(id)}`)
          : document.getElementById(id);
        if (el) observer.observe(el);
      });
    };
    if (container) observe(container);
    else
      sections.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el) observer.observe(el);
      });
    return () => observer.disconnect();
  }, [sections, scrollRootRef]);

  const scrollToSection = (id) => {
    const container = scrollRootRef?.current ?? null;
    const el = container
      ? container.querySelector(`#${CSS.escape(id)}`)
      : document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  if (!sections.length) return null;

  const jumpsBlockW = expanded ? "w-[200px]" : "w-[52px]";

  return (
    <div
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
      className={`shrink-0 overflow-hidden transition-[width] duration-200 ease-out ${jumpsBlockW} ${className}`}
    >
      <div
        className={`py-4 pr-3 ${expanded ? "w-[200px] min-w-[200px] pl-0" : "w-[52px] pl-0"}`}
      >
        {expanded && (
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3 pl-5">
            Springen zu
          </p>
        )}
        <nav
          className={`relative flex flex-col ${expanded ? "" : "items-center"}`}
          aria-label="Abschnitte"
        >
          <div
            className={`absolute top-2 bottom-2 w-px bg-gray-300 ${
              expanded ? "left-3" : "left-1/2 -translate-x-1/2"
            }`}
            aria-hidden
          />
          {sections.map(({ id, label }) => {
            const isActive = activeId === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => scrollToSection(id)}
                title={label}
                className={`relative z-10 flex items-center py-2 group min-h-[32px] rounded-lg transition-transform duration-200 ${
                  expanded ? "w-full justify-start gap-3 pl-2 -ml-2" : "w-full justify-center"
                }`}
              >
                <span
                  className={`flex items-center justify-center shrink-0 transition-transform duration-200 group-hover:scale-110 ${
                    expanded ? "w-6" : ""
                  }`}
                >
                  <span
                    className={`h-2.5 w-2.5 rounded-full transition-colors duration-200 ${
                      isActive ? "jump-dot-active" : "bg-gray-500"
                    }`}
                    aria-hidden
                  />
                </span>
                {expanded && (
                  <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900 truncate transition-all duration-200 origin-left group-hover:scale-105">
                    {label}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
